/**
 * The dialogue document (ADR-136 decision 6): one file per conversation, plain serialisable data, checked here and by
 * nothing else. A conversation is speakers, an ordered `start` that picks the opening node, and up to 64 nodes, each one
 * spoken line and up to 6 choices that lead to another node or end it. The text of every line is a line record
 * (`./line.ts`); conditions and actions are closed tagged sets, and an action stays refused until the row that builds it.
 * Pure.
 */

import { CHOICE_WEIGHTS, checkProblems, type ChoiceWeight, type DialogueCheck } from "./dialogue-check.ts";
import { companionCallProblems, companionFeedProblems, companionJoinProblems } from "./dialogue-companion.ts";
import { checkActions, checkConditions, type DialogueAction, type DialogueCondition } from "./dialogue-conditions.ts";
export type { CompanionFeed, CompanionState, DialogueAction, DialogueCondition, StandingCondition } from "./dialogue-conditions.ts";
export { COMPANION_FEEDS, COMPANION_OFFER_FEEDS, COMPANION_OFFER_STANDING, COMPANION_STATES, CONDITION_TAGS, FACT_ID_PATTERN, MAX_COMPANION_FEEDS, ITEM_CONDITION_TAGS, LATER_CONDITION_TAGS, RESERVED_ACTION_TAGS, RESERVED_CONDITION_TAGS, checkConditions } from "./dialogue-conditions.ts";
import { CONTENT_FILE_ID_PATTERN, SPEAKER_ID_PATTERN, lineProblems, type LineProblem, type LineRecord } from "./line.ts";

/**
 * The most nodes one file holds.
 */
export const MAX_DIALOGUE_NODES = 64;

/**
 * The most choices one node offers.
 */
export const MAX_DIALOGUE_CHOICES = 6;

/**
 * The most speakers one file names.
 */
export const MAX_DIALOGUE_SPEAKERS = 8;

/**
 * The most code points a speaker's `voice` note holds.
 */
export const MAX_DIALOGUE_VOICE_CHARS = 1000;

/**
 * A node's id: lower case, digits, underscores and hyphens, at most 48 characters.
 */
export const NODE_ID_PATTERN = /^[a-z0-9][a-z0-9_-]{0,47}$/;

export interface DialogueSpeaker {
  readonly id: string;
  /** The speaker's shown name, a line record with no speaker. */
  readonly name: LineRecord;
  /** A writer's note on how it speaks; never shown to players. */
  readonly voice: string;
}

interface DialogueChoiceBase {
  readonly line: LineRecord;
  readonly when: readonly DialogueCondition[];
  readonly do: readonly DialogueAction[];
  /** Marks the choice; what a weight does to the story is decided with quests. */
  readonly weight?: ChoiceWeight;
  /** Sends the choice locked with its need's text when `when` fails, instead of leaving it out. */
  readonly shownLocked?: true;
  /** The line shown as what a locked choice needs; present exactly when `shownLocked` is. */
  readonly need?: LineRecord;
}

/**
 * A choice that leads straight on.
 */
export interface DialoguePlainChoice extends DialogueChoiceBase {
  /** The node it leads to, or null to end the conversation. */
  readonly next: string | null;
}

/**
 * A choice that rolls a check and leads to `pass` or `fail`, each a node or null to end the conversation.
 */
export interface DialogueCheckChoice extends DialogueChoiceBase {
  readonly check: DialogueCheck;
  readonly pass: string | null;
  readonly fail: string | null;
}

/**
 * A choice: a plain one or one with a check.
 */
export type DialogueChoice = DialoguePlainChoice | DialogueCheckChoice;

export interface DialogueNode {
  readonly line: LineRecord;
  readonly choices: readonly DialogueChoice[];
}

export interface DialogueStart {
  readonly when: readonly DialogueCondition[];
  readonly node: string;
}

/**
 * A valid dialogue file.
 */
export interface Dialogue {
  readonly id: string;
  /** One past the highest line number the file has ever used. */
  readonly nextLine: number;
  readonly speakers: readonly DialogueSpeaker[];
  readonly start: readonly DialogueStart[];
  readonly nodes: { readonly [node: string]: DialogueNode };
}

/**
 * One fault: the file, the node it is in (null outside the nodes) and the field, a path inside the node or the file.
 */
export interface DialogueProblem {
  readonly file: string;
  readonly node: string | null;
  readonly field: string;
  readonly message: string;
}

/**
 * The verdict on one file.
 */
export type DialogueVerdict = { readonly ok: true; readonly dialogue: Dialogue } | { readonly ok: false; readonly problems: readonly DialogueProblem[] };

type Plain = { readonly [key: string]: unknown };

const TOP_FIELDS = ["id", "nextLine", "speakers", "start", "nodes"] as const;

const CHOICE_FIELDS = ["line", "when", "do", "next", "check", "pass", "fail", "weight", "shownLocked", "need"] as const;

/**
 * Whether a value is a plain object.
 *
 * @param value Anything.
 * @returns True for an object that is not null or an array.
 */
function isPlain(value: unknown): value is Plain {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Where a line record sits, for naming a problem in it.
 */
interface LineSite {
  readonly node: string | null;
  readonly field: string;
  readonly speaker: "none" | "declared";
}

/**
 * The part of a line record a line problem is about, as a field suffix.
 *
 * @param problem The line problem.
 * @returns The suffix: empty for the record itself.
 */
export function suffixOf(problem: LineProblem): string {
  switch (problem.kind) {
    case "notARecord":
      return "";
    case "invalidId":
    case "duplicateId":
    case "idNotBeforeNextLine":
      return ".id";
    case "invalidSpeaker":
      return ".speaker";
    case "invalidNote":
    case "noteTooLong":
      return ".note";
    default:
      return ".text";
  }
}

/**
 * A line problem as a sentence.
 *
 * @param problem The line problem.
 * @returns What is wrong.
 */
export function lineMessage(problem: LineProblem): string {
  switch (problem.kind) {
    case "invalidNextLine":
      return "nextLine must be a whole number from 1";
    case "notARecord":
      return "must be a line record holding exactly id, speaker, text and note";
    case "invalidId":
      return "line id must be l and a number, such as l12";
    case "duplicateId":
      return `line id ${problem.id} is used twice`;
    case "idNotBeforeNextLine":
      return `line id ${problem.id} must be below nextLine ${problem.nextLine}`;
    case "invalidSpeaker":
      return "speaker must be a speaker id or null";
    case "invalidText":
      return "text must be a string or a plural object holding other";
    case "emptyText":
      return "text must not be empty";
    case "textTooLong":
      return `text is ${problem.chars} characters; the most is ${problem.max}`;
    case "unknownVariable":
      return `text uses {${problem.name}}, which is not a line variable`;
    case "dottedPlaceholder":
      return `text uses {${problem.name}}, whose name has a dot`;
    case "malformedPlaceholder":
      return `text has a malformed placeholder (${problem.problem})`;
    case "invalidNote":
      return "note must be a string";
    case "noteTooLong":
      return `note is ${problem.chars} characters; the most is ${problem.max}`;
  }
}

/**
 * Checks a choice's `shownLocked` and `need`: both or neither, `shownLocked` only true and only on a choice with
 * conditions, since a choice with none is never locked; the need is a line record queued for the line validator.
 *
 * @param choice The choice.
 * @param report Records a problem at a path inside the node.
 * @param here The path of the choice.
 * @param collect Queues the need's line record.
 */
function lockProblems(choice: Plain, report: (field: string, message: string) => void, here: string, collect: (line: unknown) => void): void {
  const locked = choice["shownLocked"];
  const need = choice["need"];
  if (locked === undefined && need === undefined) {
    return;
  }
  if (locked !== true) {
    report(`${here}.shownLocked`, locked === undefined ? "a choice with a need must be shownLocked: true" : "shownLocked must be true or left out");
  } else if (Array.isArray(choice["when"]) && choice["when"].length === 0) {
    report(`${here}.shownLocked`, "a choice with no conditions is never locked");
  }
  if (need === undefined) {
    report(`${here}.need`, "a shownLocked choice must have a need line");
  } else {
    collect(need);
  }
}

/**
 * Validates a dialogue file: the shape, every line record by the line validator, the closed conditions and actions,
 * checks, weights and locked choices (ADR-136's amendment of 2026-10-28), the graph (every `next`, `pass`, `fail` and `start` names a node, the last `start` entry has no conditions so every character has an opening node, every node is reachable from `start`, at most 64 nodes and 6
 * choices a node), and the speakers (every node line spoken by a declared one, every choice line by none).
 *
 * @param value The parsed file.
 * @param fileId The file's name without `.json`, which `id` must equal.
 * @returns The dialogue, or every problem found, each naming the file, the node and the field.
 * @example
 * validateDialogue(JSON.parse(text), "aethling-first");
 */
export function validateDialogue(value: unknown, fileId: string): DialogueVerdict {
  const problems: DialogueProblem[] = [];
  /**
   * Records one problem against the file.
   * @param node The node it is in, or null outside the nodes.
   * @param field The field, a path inside the node or the file.
   * @param message What is wrong.
   */
  const at = (node: string | null, field: string, message: string): void => {
    problems.push({ file: fileId, node, field, message });
  };
  if (!isPlain(value)) {
    at(null, "", "a dialogue must be a JSON object");
    return { ok: false, problems };
  }
  for (const key of Object.keys(value)) {
    if (!(TOP_FIELDS as readonly string[]).includes(key)) {
      at(null, key, `${key} is not a dialogue field`);
    }
  }
  if (typeof value["id"] !== "string" || !CONTENT_FILE_ID_PATTERN.test(value["id"])) {
    at(null, "id", "id must be lower-case letters, digits, _ and -");
  } else if (value["id"] !== fileId) {
    at(null, "id", `id must be ${fileId}, the file's name, not ${value["id"]}`);
  }
  const lines: unknown[] = [];
  const sites: LineSite[] = [];
  /**
   * Queues a line record for the line validator, remembering where it sits.
   * @param line The record as read.
   * @param node The node it is in, or null.
   * @param field The path of the record.
   * @param speaker Whether its speaker must be declared or null.
   */
  const collect = (line: unknown, node: string | null, field: string, speaker: LineSite["speaker"]): void => {
    lines.push(line);
    sites.push({ node, field, speaker });
  };

  const speakerIds = new Set<string>();
  const speakers = value["speakers"];
  if (!Array.isArray(speakers) || speakers.length < 1 || speakers.length > MAX_DIALOGUE_SPEAKERS) {
    at(null, "speakers", `speakers must be a list of 1 to ${MAX_DIALOGUE_SPEAKERS} speakers`);
  } else {
    speakers.forEach((speaker, index) => {
      const here = `speakers[${index}]`;
      if (!isPlain(speaker)) {
        at(null, here, "a speaker must be an object with id, name and voice");
        return;
      }
      const id = speaker["id"];
      if (typeof id !== "string" || !SPEAKER_ID_PATTERN.test(id)) {
        at(null, `${here}.id`, "speaker id must be lower case, digits, _ and -");
      } else if (speakerIds.has(id)) {
        at(null, `${here}.id`, `speaker id ${id} is used twice`);
      } else {
        speakerIds.add(id);
      }
      collect(speaker["name"], null, `${here}.name`, "none");
      if (typeof speaker["voice"] !== "string" || [...speaker["voice"]].length > MAX_DIALOGUE_VOICE_CHARS) {
        at(null, `${here}.voice`, `voice must be text of at most ${MAX_DIALOGUE_VOICE_CHARS} characters`);
      }
    });
  }

  const nodes = value["nodes"];
  const nodeIds: string[] = [];
  const edges = new Map<string, string[]>();
  if (!isPlain(nodes)) {
    at(null, "nodes", "nodes must be an object of nodes by id");
  } else {
    const ids = Object.keys(nodes);
    if (ids.length < 1 || ids.length > MAX_DIALOGUE_NODES) {
      at(null, "nodes", `nodes must hold 1 to ${MAX_DIALOGUE_NODES} nodes, not ${ids.length}`);
    }
    for (const id of ids) {
      if (!NODE_ID_PATTERN.test(id)) {
        at(id, "node", "node id must be lower case, digits, _ and -, at most 48 characters");
        continue;
      }
      nodeIds.push(id);
    }
    const known = new Set(nodeIds);
    for (const id of ids) {
      const node = nodes[id];
      /**
       * Records one problem against this node or start entry.
       * @param field The field.
       * @param message What is wrong.
       */
      const report = (field: string, message: string): void => at(id, field, message);
      if (!isPlain(node)) {
        report("node", "a node must be an object with line and choices");
        continue;
      }
      collect(node["line"], id, "line", "declared");
      const choices = node["choices"];
      const out: string[] = [];
      if (!Array.isArray(choices)) {
        report("choices", "choices must be a list of 0 to 6 choices");
      } else {
        if (choices.length > MAX_DIALOGUE_CHOICES) {
          report("choices", `a node offers at most ${MAX_DIALOGUE_CHOICES} choices, not ${choices.length}`);
        }
        choices.forEach((choice, index) => {
          const here = `choices[${index}]`;
          if (!isPlain(choice)) {
            report(here, "a choice must be an object with line, when, do and next");
            return;
          }
          for (const key of Object.keys(choice)) {
            if (!(CHOICE_FIELDS as readonly string[]).includes(key)) {
              report(`${here}.${key}`, `${key} is not a field of a choice`);
            }
          }
          collect(choice["line"], id, `${here}.line`, "none");
          checkConditions(choice["when"], report, `${here}.when`);
          checkActions(choice["do"], report, `${here}.do`);
          /**
           * Checks one branch of the choice and records its edge.
           *
           * @param field The branch's field: next, pass or fail.
           * @returns Nothing; problems go to `report` and the edge to `out`.
           */
          const branch = (field: string): void => {
            const target = choice[field];
            if (target === null) {
              return;
            }
            if (typeof target !== "string" || !known.has(target)) {
              report(`${here}.${field}`, target === undefined ? `${field} must name a node or be null` : `${field} names ${String(target)}, which is not a node`);
            } else {
              out.push(target);
            }
          };
          if (choice["check"] === undefined) {
            branch("next");
            for (const field of ["pass", "fail"]) {
              if (choice[field] !== undefined) {
                report(`${here}.${field}`, `${field} belongs to a choice with a check; this choice has next`);
              }
            }
          } else {
            checkProblems(choice["check"], report, `${here}.check`, checkConditions, checkActions);
            if (choice["next"] !== undefined) {
              report(`${here}.next`, "a choice with a check leads on by pass and fail, not next");
            }
            branch("pass");
            branch("fail");
          }
          if (choice["weight"] !== undefined && !(CHOICE_WEIGHTS as readonly unknown[]).includes(choice["weight"])) {
            report(`${here}.weight`, `weight must be ${CHOICE_WEIGHTS.join(" or ")}`);
          }
          companionFeedProblems(choice, report, here);
          companionJoinProblems(choice, report, here);
          companionCallProblems(choice, report, here);
          lockProblems(choice, report, here, (line) => collect(line, id, `${here}.need`, "none"));
        });
      }
      edges.set(id, out);
    }
  }

  const start = value["start"];
  const starts: string[] = [];
  if (!Array.isArray(start) || start.length < 1) {
    at(null, "start", "start must be a list of at least one entry");
  } else {
    start.forEach((entry, index) => {
      const here = `start[${index}]`;
      /**
       * Records one problem against this node or start entry.
       * @param field The field.
       * @param message What is wrong.
       */
      const report = (field: string, message: string): void => at(null, field, message);
      if (!isPlain(entry)) {
        report(here, "a start entry must be an object with when and node");
        return;
      }
      checkConditions(entry["when"], report, `${here}.when`);
      const closing = index === start.length - 1;
      if (closing && Array.isArray(entry["when"]) && entry["when"].length > 0) {
        report(`${here}.when`, "the last start entry must have no conditions, so every character has an opening node");
      }
      const target = entry["node"];
      if (typeof target !== "string" || !nodeIds.includes(target)) {
        report(`${here}.node`, `node names ${String(target)}, which is not a node`);
      } else {
        starts.push(target);
      }
    });
  }

  const nextLine = value["nextLine"];
  for (const problem of lineProblems(lines, nextLine)) {
    if (problem.kind === "invalidNextLine") {
      at(null, "nextLine", lineMessage(problem));
      continue;
    }
    const site = sites[problem.index] as LineSite;
    at(site.node, `${site.field}${suffixOf(problem)}`, lineMessage(problem));
  }
  lines.forEach((line, index) => {
    const site = sites[index] as LineSite;
    if (!isPlain(line)) {
      return;
    }
    const speaker = line["speaker"];
    if (site.speaker === "declared" && (typeof speaker !== "string" || !speakerIds.has(speaker))) {
      at(site.node, `${site.field}.speaker`, speaker === null ? "a node's line must have a speaker" : `speaker ${String(speaker)} is not one of the file's speakers`);
    } else if (site.speaker === "none" && speaker !== null) {
      at(site.node, `${site.field}.speaker`, "this line has no speaker: a choice is the player's and a name is not spoken");
    }
  });

  const reached = new Set<string>(starts);
  const queue = [...starts];
  for (let head = 0; head < queue.length; head += 1) {
    for (const target of edges.get(queue[head] as string) ?? []) {
      if (!reached.has(target)) {
        reached.add(target);
        queue.push(target);
      }
    }
  }
  if (starts.length > 0) {
    for (const id of nodeIds) {
      if (!reached.has(id)) {
        at(id, "node", "no start entry or choice leads to this node");
      }
    }
  }

  return problems.length > 0 ? { ok: false, problems } : { ok: true, dialogue: value as unknown as Dialogue };
}
